import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Lib
import Cookies from 'js-cookie';
import { isDev } from 'lib/helpers';

// Constants
import { loadSeenFromLS, saveSeenToLS } from 'constans/tutorial'
import { amplitudeInstance, events } from 'constans/cookie';

type Condition = () => boolean;

interface TutorialKey {
	key: string;
	condition?: Condition;
	priority?: "low" | "normal" | "high";
	group?: "structure" | "header" | "general";
}

export type TCurrentPrice = {
	mg?: number;
	type?: string;
	label?: string;
	value?: string;
	price?: number | null;
};
export type countryItem = {
	name: string;
	region: string;
	fullName: string;
};
export type usaStateItem = {
	name: string;
	state: string;
};

// Interface
interface IState {
	user: {
		role?: string,
		user?: string,
		phone?: string,
		email?: string,
		title?: string,
		token?: string,
		country?: string,
		refresh?: string,
		lastName?: string,
		firstName?: string,
		authorized?: boolean,
		userId?: string | number,
		position?: string | number,
		organization?: string | null,
	},
	listImgs: [],
	loadingLogout: boolean,
	loadingListImgs: boolean,
	currentPrice: TCurrentPrice;
	typePrice?: string;
	isCloseSmallDropdown?: boolean,

	showFull: boolean,
	showSmall: boolean,
	activeRequests: any,
	requestsLog: any,

	countries: countryItem[] | [],
	loadingCountries: boolean,

	usaStates: usaStateItem[] | [],
	loadingUsaStates: boolean,

	paymentsToken: string,
	loadingPaymentsToken: boolean,

	tutorial: {
		seen: string[];
		keys: TutorialKey[];
	}
}

const initialCurrentPrice = {
	type: 'Usd',
	label: '5mg',
	mg: 5,
	value: 'price5mg',
	price: null
};

const keyPriceApp = 'priceApp';
const currentPrice = JSON.parse(localStorage.getItem(keyPriceApp) || JSON.stringify(initialCurrentPrice));
const auth = JSON.parse(localStorage.getItem('auth') || '{}');

const user = {
	role: '',
	user: '',
	email: '',
	phone: '',
	title: '',
	token: '',
	country: '',
	refresh: '',
	userId: '',
	position: '',
	lastName: '',
	firstName: '',
	organization: '',
	authorized: false
};

const initialState: IState = {
	user: {
		...user,
		...auth,
		authorized: !!auth.token,
	},
	listImgs: [],
	typePrice: 'EBC',
	loadingLogout: false,
	loadingListImgs: false,
	currentPrice: currentPrice,
	isCloseSmallDropdown: false,

	showFull: false,
	showSmall: false,
	activeRequests: [],
	requestsLog: {
		// 'requestUniqueId': requestLogItem
	},

	countries: [],
	loadingCountries: false,

	usaStates: [],
	loadingUsaStates: false,

	paymentsToken: '',
	loadingPaymentsToken: false,

	tutorial: {
		keys: [],
		seen: loadSeenFromLS(),
	}
};

export const settingsSlice = createSlice({
	name: 'settings',
	initialState: initialState,
	reducers: {
		markTutorialSeen: (state, action: PayloadAction<string>) => {
			if (!state.tutorial.seen.includes(action.payload)) {
				state.tutorial.seen.push(action.payload);
				saveSeenToLS(state.tutorial.seen); // 👈 сохраняем в LS
			}
		},

		addTutorialKey: (state, action: PayloadAction<TutorialKey>) => {
			const idx = state.tutorial.keys.findIndex(k => k.key === action.payload.key);

			const newKey: TutorialKey = {
				...action.payload,
				priority: action?.payload?.priority ?? "normal"
			};

			if (idx === -1) {
				state.tutorial.keys.push(newKey);
			} else {
				state.tutorial.keys[idx] = {
					...state.tutorial.keys[idx],
					condition: newKey.condition,
					priority: newKey.priority
				};
			}

			state.tutorial.keys.sort((a, b) => {
				const order = { structure: 0, general: 1, header: 2 };
				const groupA = order[a.group || "general"];
				const groupB = order[b.group || "general"];

				if (groupA !== groupB) return groupA - groupB;

				const priorityOrder = { high: 0, normal: 1, low: 2 };
				return priorityOrder[a.priority || "normal"] - priorityOrder[b.priority || "normal"];
			});
		},

		clearTutorialKeys: state => {
			state.tutorial.keys = [];
		},

		getPaymentsToken: (state, action) => {
			state.loadingPaymentsToken = true;
		},
		getPaymentsTokenError: (state) => {
			state.loadingPaymentsToken = false;
		},
		getPaymentsTokenSuccess: (state, action) => {
			state.loadingPaymentsToken = false;
			state.paymentsToken = action.payload;
		},

		getUsaStates: (state) => {
			state.loadingUsaStates = true;
		},
		getUsaStatesError: (state) => {
			state.loadingUsaStates = false;
		},
		getUsaStatesSuccess: (state, action) => {
			state.loadingUsaStates = false;
			state.usaStates = action.payload;
		},

		getAddressesCountries: (state) => {
			state.loadingCountries = true;
		},
		getAddressesCountriesError: (state) => {
			state.loadingCountries = false;
		},
		getAddressesCountriesSuccess: (state, action) => {
			state.loadingCountries = false;
			state.countries = action.payload;
		},

		handlerChangeTypePrice: (state, action) => {
			state.typePrice = action.payload;
		},

		apiResponse: (state, action) => {
			const activeRequests = action.payload.id
				? state.activeRequests.filter((id: any) => id !== action.payload.id)
				: [...state.activeRequests];

			const requestsLogNew = { ...state.requestsLog };
			if (action.payload.id) {
				requestsLogNew[action.payload.id] = {
					...requestsLogNew[action.payload.id],
					end: new Date().toISOString()
				};
			}

			return {
				...state,
				activeRequests: activeRequests,
				requestsLog: requestsLogNew
			};
		},

		apiRequest: (state, action) => {
			let requestsLog = {};
			if (isDev) {
				requestsLog = action.payload.id
					? {
						...state.requestsLog,
						[action.payload.id]: {
							id: action.payload.id,
							url: action.payload.url,
							start: new Date().toISOString()
						}
					}
					: { ...state.activeRequests };
			}

			return {
				...state,
				activeRequests: [...state.activeRequests, action.payload.id],
				requestsLog: requestsLog
			};
		},

		getListImg: (state, action) => {
			state.loadingListImgs = true;
		},
		getListImgError: (state) => {
			state.loadingListImgs = false;
		},
		getListImgSuccess: (state, action) => {
			state.listImgs = action.payload;
			state.loadingListImgs = false;
		},

		toggleModal: (state, action) => {
			state.isCloseSmallDropdown = action.payload;
		},

		changeAuthorized: (state, action) => {
			state.user.authorized = action.payload;
		},

		updateSettingsUser: (state, action) => {
			return {
				...state,
				user: {
					...state.user,
					...action.payload
				}
			};
		},

		logout: (state) => {
			state.loadingLogout = true;
		},
		logoutError: (state) => {
			state.loadingLogout = false;
		},
		logoutSuccess: (state) => {
			localStorage.removeItem('auth');

			state.user = initialState.user;

			const formData = {
				data: '',
				type: 'SIGN_OUT'
			};

			localStorage.setItem('synchronizationTab', JSON.stringify(formData));

			const isCookie = Cookies.get('cookieName');
			if (isCookie) {
				amplitudeInstance.logEvent(events.logout);
			}
		},
		updateLogout: (state) => {
			localStorage.removeItem('auth');

			state.user = initialState.user;
		},

		handlerChangePriceApp: (state, action) => {
			const selected = action.payload;
			state.currentPrice = selected;

			localStorage.setItem(keyPriceApp, JSON.stringify(selected));
		}
	}
});

export const {
	addTutorialKey,
	markTutorialSeen,
	clearTutorialKeys,

	getPaymentsToken,
	getPaymentsTokenError,
	getPaymentsTokenSuccess,

	getUsaStates,
	getUsaStatesError,
	getUsaStatesSuccess,

	getAddressesCountries,
	getAddressesCountriesError,
	getAddressesCountriesSuccess,

	handlerChangeTypePrice,

	apiRequest,
	apiResponse,

	logout,
	getListImg,
	toggleModal,
	logoutError,
	updateLogout,
	logoutSuccess,
	getListImgError,
	changeAuthorized,
	getListImgSuccess,
	updateSettingsUser,
	handlerChangePriceApp
} = settingsSlice.actions;

export default settingsSlice.reducer;
